import { useRef, useState, useCallback } from 'react';

/**
 * Hook to prevent multiple form submissions using a ref-based lock.
 * Works immediately even before state updates propagate, preventing rapid double-clicks.
 */
export function useFormSubmitLock() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const lockRef = useRef(false);

  const withSubmitLock = useCallback(<T extends any[], R>(
    fn: (...args: T) => Promise<R>
  ) => {
    return async (...args: T): Promise<R | undefined> => {
      // Check lock immediately (doesn't wait for state)
      if (lockRef.current) {
        console.warn('Form submission already in progress, ignoring duplicate request');
        return undefined;
      }

      // Acquire lock
      lockRef.current = true;
      setIsSubmitting(true);

      try {
        const result = await fn(...args);
        return result;
      } finally {
        // Always release lock, even on error
        lockRef.current = false;
        setIsSubmitting(false);
      }
    };
  }, []);

  return {
    isSubmitting,
    withSubmitLock,
  };
}
